
import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
} from "react-bootstrap";
import { ArrowCounterclockwise, Wallet, Ban } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const RefundModal = ({
  show,
  onHide,
  purchase,
  role = "ADMIN",
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [chargeUser, setChargeUser] = useState(true);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const purchaseAmount = Number(purchase?.amount || 0);

  const refundedAmount = refunds.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const remainingAmount = Math.max(
    0,
    purchaseAmount - refundedAmount
  );

  const numericAmount = Number(amount || 0);

  useEffect(() => {
    if (!show || !purchase?.id) return;

    const loadRefunds = async () => {
      setLoading(true);

      try {
        const { data } = await api.get(
          `/wallet/refunds/${purchase.id}`
        );

        setRefunds(data.refunds || []);
      } catch (error) {
        toast.error("دریافت سوابق برگشت سفارش ناموفق بود");
      } finally {
        setLoading(false);
      }
    };

    setAmount("");
    setDescription("");
    setChargeUser(true);
    loadRefunds();
  }, [show, purchase?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!numericAmount || numericAmount <= 0) {
      toast.error("مبلغ برگشت باید بیشتر از صفر باشد");
      return;
    }

    if (numericAmount > remainingAmount) {
      toast.error("مبلغ برگشت بیشتر از مانده قابل برگشت است");
      return;
    }

    const payload = {
      purchaseId: Number(purchase.id),
      amount: numericAmount,
      description: description.trim() || undefined,
      chargeUser,
    };

    if (role === "ADMIN") {
      payload.storeId = Number(purchase.store?.id);
    }

    setSaving(true);

    try {
      await api.post(
        role === "ADMIN"
          ? "/wallet/admin/refund"
          : "/wallet/store/refund",
        payload
      );

      toast.success("برگشت سفارش با موفقیت ثبت شد");

      onHide();
      onSuccess?.();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(
        Array.isArray(message)
          ? message.join("، ")
          : message || "ثبت برگشت سفارش ناموفق بود"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!purchase) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-semibold">
            <ArrowCounterclockwise className="me-2 text-danger" />
            برگشت سفارش
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="bg-light rounded-3 p-3 mb-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">شماره تراکنش</span>
              <strong>#{purchase.id}</strong>
            </div>

            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">مبلغ خرید</span>
              <strong>
                {purchaseAmount.toLocaleString()} تومان
              </strong>
            </div>

            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">مجموع برگشتی قبلی</span>
              <span className="text-danger fw-semibold">
                {refundedAmount.toLocaleString()} تومان
              </span>
            </div>

            <hr className="my-2" />

            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">مانده قابل برگشت</span>
              <strong className="text-success">
                {remainingAmount.toLocaleString()} تومان
              </strong>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              {remainingAmount <= 0 && (
                <Alert variant="warning" className="small">
                  کل مبلغ این خرید قبلاً برگشت داده شده است.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  مبلغ برگشت (تومان)
                </Form.Label>

                <Form.Control
                  type="number"
                  min="1"
                  max={remainingAmount}
                  step="1"
                  required
                  disabled={remainingAmount <= 0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="مبلغ موردنظر را وارد کنید"
                />

                <Form.Text className="text-muted">
                  حداکثر مبلغ مجاز:{" "}
                  {remainingAmount.toLocaleString()} تومان
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  نحوه برگشت مبلغ
                </Form.Label>

                <div className="d-flex flex-column gap-2">
                  <Form.Check
                    type="radio"
                    id="refund-charge"
                    name="refundMode"
                    className="border border-success rounded-3 p-3 ps-5"
                    checked={chargeUser === true}
                    onChange={() => setChargeUser(true)}
                    disabled={remainingAmount <= 0}
                    label={
                      <div>
                        <div className="text-success fw-semibold">
                          <Wallet className="me-2" />
                          شارژ اعتبار در حساب کاربر
                        </div>
                        <small className="text-muted">
                          مبلغ برگشتی به اعتبار کاربر اضافه می‌شود.
                        </small>
                      </div>
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="refund-no-charge"
                    name="refundMode"
                    className="border border-danger rounded-3 p-3 ps-5"
                    checked={chargeUser === false}
                    onChange={() => setChargeUser(false)}
                    disabled={remainingAmount <= 0}
                    label={
                      <div>
                        <div className="text-danger fw-semibold">
                          <Ban className="me-2" />
                          بدون برگشت اعتبار
                        </div>
                        <small className="text-muted">
                          فقط سفارش برگشت می‌خورد و اعتباری به کاربر اضافه نمی‌شود.
                        </small>
                      </div>
                    }
                  />
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Label>توضیحات (اختیاری)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="دلیل برگشت سفارش..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="submit"
            variant="danger"
            disabled={
              loading ||
              saving ||
              remainingAmount <= 0 ||
              !numericAmount ||
              numericAmount > remainingAmount
            }
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                در حال ثبت...
              </>
            ) : (
              "ثبت برگشت سفارش"
            )}
          </Button>

          <Button
            variant="light"
            onClick={onHide}
            disabled={saving}
          >
            انصراف
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RefundModal;
